/**
 * WHAT GETS LOOKED AT.
 *
 * Two catalogs merged, because each had something the other did not.
 *
 * The old CRM's list is what a working shop actually checks — it knows
 * about pitman arms and timing belts and which corner a link pin is on. It
 * is also a wall of text, which is why nobody finished one.
 *
 * The PPI tool at prepurchaseauto.com/inspection is the shape that gets
 * finished: sections you can collapse, an icon to find your place, and a
 * count per section so you can see what is left without reading it.
 *
 * So: the PPI's shape, the shop's depth.
 *
 * VERDICTS ARE THE OLD SYSTEM'S FOUR, kept deliberately. "Good / worth
 * doing / needs doing / not fitted" is what a technician actually decides,
 * and the middle two are the whole commercial value of an inspection —
 * collapsing them into "fail" loses the difference between "this will need
 * doing by winter" and "do not drive this".
 *
 * TYRES ARE OPTIONAL. Anthony: "leave tires as optional to complete
 * inspection for now please." They are their own section, they carry
 * measurements rather than verdicts, and the finish check ignores them.
 */

export type Verdict = "good" | "watch" | "now" | "na";

export const VERDICTS: Array<{ key: Verdict; short: string; label: string; what: string }> = [
  { key: "good", short: "OK", label: "Good", what: "Looked at, nothing wrong" },
  { key: "watch", short: "SOON", label: "Worth doing", what: "Will need doing, not urgent" },
  { key: "now", short: "NOW", label: "Needs doing", what: "Before this car is safe" },
  { key: "na", short: "N/A", label: "Not applicable", what: "Not fitted, or could not be reached" },
];

/** A mark that costs the customer money has to say why. */
export const NEEDS_REASON: Verdict[] = ["watch", "now"];

export type Point = { key: string; name: string };

export type Section = {
  id: string;
  icon: string;
  title: string;
  /** Left out of the "is it finished" count. */
  optional?: boolean;
  /** Tread depths rather than verdicts. */
  measured?: boolean;
  points: Point[];
};

const p = (key: string, name: string): Point => ({ key, name });

export const SECTIONS: Section[] = [
  {
    id: "engine", icon: "⚙️", title: "Engine",
    points: [
      p("oil_level", "Oil level and condition"),
      p("oil_leaks", "Oil leaks"),
      p("air_filter", "Air filter"),
      p("belts", "Belts"),
      p("belt_tensioner", "Belt tensioner"),
      p("spark_plugs", "Spark plugs"),
      p("ignition_wires", "Ignition wires"),
      p("vacuum_lines", "Vacuum hoses and lines"),
      p("valve_covers", "Valve cover gaskets"),
      p("intake_gaskets", "Intake gaskets"),
      p("motor_mounts", "Motor mounts"),
      p("timing_belt", "Timing belt"),
      p("exhaust_leaks", "Exhaust leaks"),
    ],
  },
  {
    id: "cooling", icon: "🌡️", title: "Cooling",
    points: [
      p("coolant_level", "Coolant level"),
      p("coolant_hoses", "Hoses"),
      p("radiator", "Radiator"),
      p("water_pump", "Water pump"),
      p("cooling_fan", "Cooling fan"),
      p("cap_thermostat", "Cap and thermostat"),
    ],
  },
  {
    id: "brakes", icon: "🛑", title: "Brakes",
    points: [
      p("brake_fluid", "Fluid level and condition"),
      p("brake_leaks", "Fluid leaks"),
      p("front_pads", "Front pads"),
      p("front_rotors", "Front rotors"),
      p("rear_linings", "Rear pads or shoes"),
      p("rear_rotors", "Rear rotors or drums"),
      p("calipers", "Calipers"),
      p("brake_lines", "Lines and hoses"),
      p("master_cylinder", "Master cylinder"),
      p("parking_brake", "Parking brake"),
    ],
  },
  {
    id: "steering", icon: "🌀", title: "Steering and suspension",
    points: [
      p("shocks_struts", "Shocks and struts"),
      p("ball_joints", "Ball joints"),
      p("tie_rods", "Tie rod ends"),
      p("control_arms", "Control arms and bushings"),
      p("sway_bar", "Sway bar and links"),
      p("rack_pinion", "Rack and pinion"),
      p("ps_fluid", "Power steering fluid"),
      p("ps_hoses", "Power steering hoses"),
      p("wheel_bearings", "Wheel bearings"),
      p("cv_boots", "CV boots"),
      p("cv_joints", "CV joints"),
      p("u_joints", "U-joints"),
    ],
  },
  {
    id: "drivetrain", icon: "⚡", title: "Drivetrain",
    points: [
      p("trans_fluid", "Transmission fluid"),
      p("trans_shifting", "Shifting"),
      p("trans_mounts", "Transmission mounts"),
      p("driveshaft", "Driveshaft"),
      p("diff_fluid", "Differential fluid"),
      p("four_wheel", "Four wheel drive system"),
      p("clutch", "Clutch"),
    ],
  },
  {
    id: "electrical", icon: "🔌", title: "Electrical",
    points: [
      p("battery_test", "Battery test"),
      p("battery_terminals", "Terminals and cables"),
      p("alternator", "Alternator output"),
      p("starter", "Starter"),
      p("headlights", "Headlights"),
      p("exterior_lights", "Brake, turn and marker lights"),
      p("horn", "Horn"),
      p("wipers", "Wipers and washers"),
      p("power_windows", "Power windows and locks"),
    ],
  },
  {
    id: "ac", icon: "❄️", title: "Heating and air conditioning",
    points: [
      p("ac_cooling", "Cools properly"),
      p("ac_compressor", "Compressor"),
      p("cabin_filter", "Cabin filter"),
      p("heater", "Heater"),
      p("blower", "Blower motor"),
    ],
  },
  {
    id: "body", icon: "🚗", title: "Body, glass and interior",
    points: [
      p("glass_chips", "Glass"),
      p("mirrors", "Mirrors"),
      p("wiper_blades", "Wiper blades"),
      p("seat_belts", "Seat belts"),
      p("body_damage", "Body and paint"),
      p("undercarriage", "Undercarriage and rust"),
      p("frame", "Frame"),
    ],
  },
  {
    id: "tires", icon: "🛞", title: "Tires and wheels", optional: true, measured: true,
    points: [
      p("tire_lf", "Left front"),
      p("tire_rf", "Right front"),
      p("tire_lr", "Left rear"),
      p("tire_rr", "Right rear"),
      p("tire_spare", "Spare"),
    ],
  },
];

/** Every point that has to be answered before this counts as finished. */
export const REQUIRED_POINTS: string[] = SECTIONS
  .filter((s) => !s.optional)
  .flatMap((s) => s.points.map((x) => x.key));

export const sectionOf = (pointKey: string): Section | undefined =>
  SECTIONS.find((s) => s.points.some((x) => x.key === pointKey));

export const pointName = (pointKey: string): string =>
  SECTIONS.flatMap((s) => s.points).find((x) => x.key === pointKey)?.name ?? pointKey;

/**
 * Tread depth, read the way a shop reads it. Under 2/32 is not roadworthy
 * in most states, and 4/32 is where a tire stops working in the wet — the
 * number nobody outside the trade knows and the reason a car aquaplanes in
 * November on tires that "still have tread on them".
 */
export function treadVerdict(thirtySeconds: number): { verdict: Verdict; says: string } {
  if (!(thirtySeconds > 0)) return { verdict: "na", says: "not measured" };
  if (thirtySeconds <= 2) return { verdict: "now", says: "legally worn out" };
  if (thirtySeconds <= 4) return { verdict: "watch", says: "poor in the wet" };
  if (thirtySeconds <= 6) return { verdict: "watch", says: "over half worn" };
  return { verdict: "good", says: "plenty left" };
}
